import { VdpVehicle } from "../../../global"
import { firstLettersCapital, toUsd } from "../../../helpers"

type ProcessedResults = {
  vehicle_model?: string[]
  vehicle_exterior_color?: string[]
  vehicle_year?: string[]
  vehicle_interior_color?: string[]
  vehicle_trim?: string[]
  vehicle_trim_id?: string[]
  vehicle_vin?: string[]
  vehicle_sale_price?: string[]
  vehicle_avail?: string | string[]
  search_results?: number | null
  vehicle_segment_vehicle_page?: string
  allocated_dealer_code?: string[]
  allocated_dealer_name?: string[]
  dealer_code?: string[]
  dealer_name?: string[]
}

/**
 * Convert boolean value to T/F/N string
 * @param {boolean | undefined | null} value The boolean value to convert
 * @returns {string} "T" for true, "F" for false, "N" for null/undefined
 */
export const boolToTFN = (value: boolean | undefined | null): string => {
  if (value === true) return "T"
  if (value === false) return "F"
  return "N"
}

/**
 * Generate similar strength string for Tealium tracking
 * @param {VdpVehicle} vehicle The vehicle object
 * @param {number} totalMatches Number of matched filter categories
 * @param {number} totalFilters Number of searched filter categories
 * @param {boolean} availabilityMatch Whether availability matches
 * @param {Object} selectedFilters Object containing currently selected filters by the user
 * @returns {string} Pipe-delimited string with match information
 */
export const generateSimilarStrength = (
  vehicle: VdpVehicle,
  totalMatches: number,
  totalFilters: number,
  availabilityMatch: boolean,
  selectedFilters: { [key: string]: any } = {},
): string => {
  // Helper function to determine if a filter category was selected and matches
  const getFilterStatus = (
    isMatch: boolean | undefined | null,
    filterKeys: string[],
    selectedFilters: { [key: string]: any },
  ): string => {
    // Check if any of the filter keys were selected by the user
    const isSelected = filterKeys.some(
      key =>
        selectedFilters?.hasOwnProperty(key) &&
        selectedFilters[key] !== null &&
        selectedFilters[key] !== undefined &&
        (Array.isArray(selectedFilters[key])
          ? selectedFilters[key].length > 0
          : true),
    )
    
    if (!isSelected) {
      return "N" // Not selected by user
    }

    // Filter was selected, check if it matches
    return isMatch ? "T" : "F"
  }

  // Helper function to check if any filters are currently selected
  const hasAnyFiltersSelected = (): boolean => {
    const filterKeys = [
      "priceMin",
      "priceMax",
      "available",
      "year",
      "modelNumber",
      "accessoryPIOs",
      "accessoryFIAs",
      "exteriorColor",
      "interiorColor",
    ]
    return filterKeys.some(key => {
      const value = selectedFilters[key]
      return (
        value !== null &&
        value !== undefined &&
        (Array.isArray(value) ? value.length > 0 : true)
      )
    })
  }

  // Calculate accessory match using the same logic as InventoryResultCard
  const isAccessoryFilterSelected =
    selectedFilters?.accessoryPIOs || selectedFilters?.accessoryFIAs
  const isAccessoryMatch =
    isAccessoryFilterSelected &&
    (selectedFilters?.accessoryPIOs || []).every((accessoryCode: string) =>
      vehicle.accessories?.some(
        (accessory: { code: string }) => accessory.code === accessoryCode,
      ),
    ) &&
    (selectedFilters?.accessoryFIAs || []).every((accessoryCode: string) =>
      vehicle.accessories?.some(
        (accessory: { code: string }) => accessory.code === accessoryCode,
      ),
    )

  // For the first value, only return "N" if no filters are currently selected
  const firstValue = hasAnyFiltersSelected()
    ? boolToTFN(vehicle.is_exact_match)
    : "N"

  return [
    firstValue,
    totalMatches,
    totalFilters,
    getFilterStatus(
      vehicle.is_price_match,
      ["priceMin", "priceMax"],
      selectedFilters,
    ),
    getFilterStatus(availabilityMatch, ["available"], selectedFilters),
    getFilterStatus(vehicle.is_year_match, ["year"], selectedFilters),
    getFilterStatus(
      vehicle.is_model_number_match,
      ["modelNumber"],
      selectedFilters,
    ),
    getFilterStatus(
      isAccessoryMatch,
      ["accessoryPIOs", "accessoryFIAs"],
      selectedFilters,
    ),
    getFilterStatus(
      vehicle.is_exterior_color_match,
      ["exteriorColor"],
      selectedFilters,
    ),
    getFilterStatus(
      vehicle.is_interior_color_match,
      ["interiorColor"],
      selectedFilters,
    ),
  ].join("|")
}

/**
 * Use vehicle inventory data to create an object for Tealium calls
 *
 * @param {Array} data An array of vehicles, from the vehicle Inventory query
 * @param {number} numberOfResults The number of vehicles returned by the vehicle inventory query
 *
 * @returns {ProcessedResults}  An Object with re-formatted vehicle data
 */

export const processVehiclesData = (data: any[], numberOfResults: number) => {
  const processed: ProcessedResults = {
    vehicle_model: [],
    vehicle_exterior_color: [],
    vehicle_year: [],
    vehicle_interior_color: [],
    vehicle_trim: [],
    vehicle_trim_id: [],
    vehicle_vin: [],
    vehicle_sale_price: [],
    search_results: null,
    allocated_dealer_code: [],
    allocated_dealer_name: [],
  }

  const availabilityArray: string[] = []

  processed.search_results = numberOfResults || 0
  // TODO: get dealer names from the API, not from Location context
  data?.forEach((vehicle: VdpVehicle) => {
    processed?.vehicle_model?.push(vehicle?.vehicles_seriesname)
    processed?.vehicle_exterior_color?.push(vehicle?.color_exterior_name)
    processed?.vehicle_interior_color?.push(vehicle?.color_interior_name)
    processed?.vehicle_year?.push(vehicle?.vehicles_modelyear.toString())
    processed?.vehicle_trim?.push(vehicle?.trim_grade.toLowerCase())
    processed?.vehicle_trim_id?.push(vehicle?.vehicles_modelnumber.toString())
    processed?.vehicle_vin?.push(vehicle?.vehicles_vin?.toString())
    processed?.vehicle_sale_price?.push(`$${toUsd(vehicle?.trim_basemsrp)}`)
    availabilityArray.push(
      vehicle?.available?.toLowerCase() === "now" ? "Available Now" : "Available Soon"
    )
    processed?.allocated_dealer_code?.push(vehicle?.dealer_set_dlr_nbr)
    processed?.allocated_dealer_name?.push(
      firstLettersCapital(vehicle?.dealer_dlr_nm),
    )
  })

  // Convert availability array to JSON array string
  processed.vehicle_avail = availabilityArray.length > 0
    ? availabilityArray
    : []

  return processed
}
// ... existing code ...

/**
 * Maps color codes to their corresponding color categories
 *
 * @param {string | string[]} colorCodes - Single color code or array of color codes to map
 * @returns {string | string[]| null} - Single color category or array of color categories corresponding to the input
 */
export const mapColorCodesToCategories = (
  colorCodes: string | string[],
): string | string[] | null => {
  const colorCodeMap: { [key: string]: string } = {
    "0040": "WhiteMixTone_standard",
    "0070": "White_extra",
    "0089": "White_mixCost",
    "0090": "White_extra",
    "0092": "Yellow_standard",
    "01D6": "Gray_standard",
    "01F7": "Gray_standard",
    "01G3": "Gray_standard",
    "01H1": "Gray_standard",
    "01H5": "Gray_standard",
    "01J6": "Gray_extra",
    "01J9": "Gray_standard",
    "01K3": "Gray_standard",
    "01K4": "Gray_standard",
    "01K5": "Gray_extra",
    "01K6": "White_standard",
    "01K9": "Gray_standard",
    "01L0": "Gray_standard",
    "01L5": "Gray_extra",
    "01L6": "Blue_standard",
    "01L7": "Gray_standard",
    "01M1": "Gray_extra",
    "01M2": "Gray_standard",
    "0202": "Black_standard",
    "0209": "Black_standard",
    "0218": "BlackMixTone_standard",
    "0219": "Black_mixCost",
    "0221": "Blue_standard",
    "02KR": "White2Tone_extra",
    "02KS": "Gray2Tone_extra",
    "02MQ": "White2Tone_mixCost",
    "02MR": "Gray2Tone_extra",
    "02NA": "White2Tone_extra",
    "02NB": "Gray2Tone_extra",
    "02NH": "Blue2Tone_extra",
    "02NR": "White2Tone_mixCost",
    "02NU": "Red2Tone_extra",
    "02PN": "Red2Tone_extra",
    "02PS": "White2Tone_extra",
    "02PT": "Gray2Tone_extra",
    "02PU": "White2Tone_extra",
    "02QJ": "White2Tone_extra",
    "02QS": "Gray2Tone_standard",
    "02QU": "Gray2Tone_extra",
    "02QV": "Blue2Tone_extra",
    "02QW": "Black2Tone_extra",
    "02QX": "Gray2Tone_extra",
    "02QY": "Gray2Tone_standard",
    "02QZ": "Gray2Tone_mixCost",
    "02RA": "Blue2Tone_mixCost",
    "02RC": "Blue2Tone_extra",
    "02SC": "Red2Tone_extra",
    "02TA": "Black2Tone_extra",
    "02TB": "Red2Tone_extra",
    "02TK": "Brown2Tone_extra",
    "02TT": "Blue2Tone_extra",
    "02TU": "Blue2Tone_extra",
    "02UE": "White2Tone_extra",
    "02UF": "Gray2Tone_extra",
    "02UH": "Blue2Tone_extra",
    "02UP": "Gray2Tone_extra",
    "02UQ": "Blue2Tone_extra",
    "02UZ": "White2Tone_extra",
    "02VP": "White2Tone_extra",
    "02VV": "Blue2Tone_mixCost",
    "02WC": "Gray2Tone_extra",
    "02XM": "Red2Tone_extra",
    "02XW": "White2Tone_extra",
    "02XZ": "Brown2Tone_extra",
    "02YC": "Gray2Tone_extra",
    "02YD": "Red2Tone_extra",
    "02YE": "Gray2Tone_extra",
    "02YJ": "Gray2Tone_extra",
    "02YZ": "Red2Tone_extra",
    "02ZA": "Gray2Tone_extra",
    "02ZC": "Brown2Tone_extra",
    "02ZD": "Blue2Tone_extra",
    "02ZW": "Gray2Tone_mixCost",
    "02ZX": "Brown2Tone_extra",
    "03R3": "RedMixTone_standard",
    "03T3": "Red_mixCost",
    "03U5": "Red_extra",
    "03U9": "Red_extra",
    "04U3": "Brown_standard",
    "04V8": "Gray_standard",
    "04W5": "Red_mixCost",
    "04X1": "Gray_standard",
    "04X4": "Brown_standard",
    "04X9": "Brown_standard",
    "04Y6": "Brown_extra",
    "04Y8": "Red_standard",
    "04Z0": "Brown_standard",
    "04Z2": "Brown_standard",
    "0588": "Green_standard",
    "05C3": "Green_standard",
    "05C5": "Yellow_standard",
    "06V7": "Green_standard",
    "06X1": "Brown_standard",
    "06X3": "Gray_standard",
    "06X3OF": "Gray_standard",
    "06X3SR": "Gray_standard",
    "06X5": "Green_standard",
    "06X7": "Green_standard",
    "0781": "Gray_standard",
    "0791": "Blue_standard",
    "0795": "Blue_standard",
    "08P8": "Blue_standard",
    "08Q4": "Blue_standard",
    "08S6": "Blue_standard",
    "08T6": "Blue_standard",
    "08V5": "Blue_standard",
    "08W2": "Blue_standard",
    "08W7": "Blue_standard",
    "08W9": "Blue_standard",
    "08X0": "Blue_standard",
    "08X2": "Blue_standard",
    "08X7": "Blue_standard",
    "08X8": "Blue_standard",
    "0D01": "White_standard",
    "0D02": "Gray_standard",
    "0D03": "Gray_standard",
    "0D04": "Black_standard",
    "0D05": "Red_standard",
    "0D06": "Yellow_extra",
    "0D08": "Gray_extra",
    "0D10": "Gray_standard",
    "0D11": "Black_standard",
    "0D12": "Gray_extra",
    "0D13": "Blue_extra",
    "0D14": "Gray_standard",
    "0D15": "Blue2Tone_extra",
    "0D16": "Red2Tone_extra",
    "0D17": "Gray2Tone_extra",
    "0D18": "Yellow2Tone_extra",
    "0D19": "Red_standard",
    "0D20": "Gray2Tone_extra",
    "0D23": "White2Tone_extra",
    "0D25": "Red_extra",
    "0D28": "Red2Tone_extra",
    "0D4S": "Black_standard",
    "0DAR": "Blue_standard",
    "0DCK": "Red_extra",
    "0DMG": "Green_standard",
    "0G1U": "Gray_standard",
    "0K1X": "Gray_extra",
    "0M02": "Green2Tone_extra",
    "0M04": "Blue2Tone_extra",
    "0M16": "Brown2Tone_standard",
    "0M30": "Green2Tone_extra",
    "0P8Y": "Gray_standard",
    "0WCH": "Blue_standard",
    "0WCL": "Brown_extra",
    "0XDA": "Blue_standard",
    AM20: "Black",
    Black: "Black",
    blackleather: "Black",
    blacksoftex: "Black",
    boulder: "Gray",
    bouldersoftex: "Gray",
    EA00: "Brown",
    EA10: "Gray",
    EA11: "Gray",
    EA13: "Gray",
    EA15: "Gray",
    EA20: "Black_Gray",
    EA21: "Black_Blue",
    EA22: "Black_Red",
    EA24: "Black",
    EA30: "Black_Red",
    EA40: "Beige_Black_Brown",
    EB10: "Gray",
    EB11: "Gray",
    EB12: "Black_Gray",
    EB13: "Black_Gray",
    EB16: "Gray",
    EB20: "Black_Gray",
    EB21: "Black_Blue_Red",
    EB22: "Black",
    EB24: "Black",
    EB26: "Black_Red",
    EB30: "Red",
    EB33: "Red",
    EB40: "Beige_Black_Brown",
    EB41: "Brown",
    EB43: "Black_Brown",
    EB63: "Gray",
    EC10: "Gray",
    EC13: "Gray",
    EC20: "Black_Blue_Gray_Red",
    EC21: "Black_Red",
    EC22: "Black",
    EC30: "Red",
    EC40: "Black_Brown",
    EC60: "Gray",
    ED20: "Black",
    ED22: "Black",
    ED27: "Black",
    ED30: "Red",
    FA10: "Gray",
    FA13: "Gray",
    FA14: "Gray",
    FA15: "Gray",
    FA20: "Black",
    FA21: "Black",
    FA23: "Black_Red",
    FA40: "Beige_Brown",
    FB00: "Brown",
    FB10: "Gray",
    FB11: "Black_Blue_Gray",
    FB12: "Black_Gray",
    FB13: "Gray",
    FB14: "Gray",
    FB20: "Black_Blue",
    FB21: "Black",
    FB22: "Black_Gray_Red",
    FB40: "Beige",
    FB42: "Beige",
    FB44: "Beige",
    FC10: "Gray",
    FC11: "Black_Blue_Gray",
    FC12: "Black_Gray",
    FC13: "Gray",
    FC14: "Gray",
    FC20: "Black",
    FC22: "Black_Gray",
    FC23: "Black_Gray_Red",
    FC24: "Gray",
    FC26: "Black_Red",
    FC28: "Black",
    FC44: "Beige",
    FD10: "Gray",
    FD11: "Black_Blue_Gray",
    FD12: "Black_Gray",
    FD17: "Black",
    FD20: "Black",
    FD21: "Black",
    FD26: "Black_Red",
    FD40: "Beige",
    FE11: "Black_Blue_Gray",
    FE12: "Black_Gray",
    FE21: "Black",
    FF10: "Gray",
    FF20: "Black",
    FF22: "Black_Gray",
    FG13: "Gray",
    FG44: "Beige",
    FH21: "Black",
    FH25: "Black",
    FK80: "Black_Blue",
    Gray: "Gray",
    grayleather: "Gray",
    LA00: "Brown",
    LA10: "Gray",
    LA13: "Gray",
    LA20: "Black",
    LA21: "Black",
    LA22: "Black_Gray",
    LA23: "Black",
    LA24: "Black_Brown_Red",
    LA25: "Black",
    LA26: "Black",
    LA30: "Black_Red",
    LA40: "Beige_Brown",
    LA41: "Brown",
    LA42: "Beige_Brown",
    LA43: "Brown",
    LA44: "Beige",
    LA45: "Brown",
    LB10: "Gray",
    LB13: "Gray",
    LB20: "Black",
    LB22: "Black_Gray",
    LB25: "Black_Red",
    LB30: "Red",
    LB40: "Beige_Brown",
    LB41: "Beige_Brown",
    LB42: "Beige_Brown",
    LB44: "Beige_Brown",
    LB81: "Blue",
    LC10: "Gray",
    LC13: "Gray",
    LC20: "Black",
    LC22: "Black_Gray",
    LC23: "Black",
    LC25: "Black",
    LC27: "Black",
    LC30: "Red",
    LC44: "Beige",
    LD20: "Black",
    LD21: "Black_Brown",
    LD23: "Black",
    LD24: "Black",
    LD42: "Brown",
    LD47: "Brown",
    LE13: "Gray",
    LE20: "Black",
    LE44: "Beige",
    LF20: "Black_Red",
    LF22: "Black_Gray",
    LG13: "Gray",
    LG20: "Black",
    LG23: "Black",
    LG44: "Beige",
    LH13: "Gray",
    LH21: "Black_Red",
    LH44: "Beige",
    LJ23: "Black",
    LJ42: "Brown",
    LK22: "Black_Gray",
    LL22: "Black_Gray",
    Tan: "Beige",
    tanleather: "Beige",
    trailblack: "Black",
    WB25: "Black",
  }

  // Handle single string input
  if (typeof colorCodes === "string") {
    return colorCodeMap[colorCodes] || null
  }

  // Handle array input
  return colorCodes
    .map(code => colorCodeMap[code] || null)
    .filter(category => category !== null)
}

// ... existing code ...
